import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ShareReadingRequest } from '@tarot/shared';
import { shareReading } from '../../lib/api';
import { useMe } from '../auth/useMe';
import { hasStashedReading, takeStashedReading } from './pendingSave';

/**
 * 해석이 끝나면 결과를 **미리 발행**하고 shareId를 돌려준다 (회원·비회원 모두).
 *
 * 회원에게는 지난 타로 자동 저장을 겸한다(`host_user_id` 연결). 비회원은 히스토리에 남지 않고
 * 공유 링크로만 존재한다.
 *
 * 비회원까지 미리 발행하는 이유는 **iOS 때문이다.** Safari는 `navigator.share()`를 사용자 조작과
 * 같은 실행 흐름에서만 허용한다. 공유를 누른 뒤에야 발행하면 그 `await` 때문에 조작과의 연결이
 * 끊겨 공유 시트가 거절당한다(“공유하지 못했어요”). 링크를 미리 만들어 두면 누르는 순간
 * 기다릴 것이 없어 공유 시트가 바로 열린다. (2026-08-21)
 *
 * 발행은 `auto: true` — 사용자가 실제로 누른 공유와 구분해 통계(share:publish)에서 제외한다.
 */
export function usePublishReading(shareData: ShareReadingRequest | null): string | null {
  const { me } = useMe();
  const qc = useQueryClient();
  const [shareId, setShareId] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!shareData || shareId || inFlight.current) return;
    inFlight.current = true;
    shareReading({ ...shareData, auto: true })
      .then((r) => {
        setShareId(r.shareId);
        if (me) void qc.invalidateQueries({ queryKey: ['readings'] }); // 지난 타로 목록 갱신
      })
      .catch(() => {
        /* 실패해도 조용히 — 공유 버튼이 그때 다시 발행한다 */
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [me, shareData, shareId, qc]);

  return shareId;
}

/**
 * 로그인하러 가기 전에 맡겨둔 결과를 발행한다 (앱 전역에서 1회).
 * OAuth 리다이렉트로 돌아오면 방 화면이 아니라 홈에 도착하므로,
 * 방 컴포넌트가 아닌 App 레벨에서 처리해야 한다.
 */
export function useSavePendingReading(): void {
  const { me } = useMe();
  const qc = useQueryClient();
  const done = useRef(false);

  useEffect(() => {
    if (!me || done.current || !hasStashedReading()) return;
    const data = takeStashedReading();
    if (!data) return;
    done.current = true;
    shareReading({ ...data, auto: true })
      .then(() => qc.invalidateQueries({ queryKey: ['readings'] }))
      .catch(() => {
        /* 실패해도 조용히 — 사용자를 막지 않는다 */
      });
  }, [me, qc]);
}
